const COLON_ANCHORS = {
  1: [197, 262], 2: [181, 262], 3: [219, 306], 4: [170, 306],
  5: [149, 205], 6: [154, 90], 7: [269, 131], 8: [380, 47],
  9: [380, 190], 10: [344, 315], 11: [374, 283], 12: [270, 336],
  13: [270, 415], 14: [49, 346], 15: [49, 365], 16: [272, 378],
}

const GASTRO_ANCHORS = {
  1: [202, 237], 2: [198, 171], 3: [197, 206], 4: [202, 237],
  5: [204, 269], 6: [217, 348], 7: [263, 323], 8: [260, 395],
  9: [269, 361], 10: [269, 361], 11: [260, 395], 12: [238, 428],
  13: [212, 402], 14: [212, 442], 15: [154, 407], 16: [146, 428],
  17: [146, 428], 18: [146, 428], 19: [209, 311],
}

export const ANATOMY_MAP_CONFIG = {
  colon: {
    image: '/img/colon-preview-img.png',
    imageWidth: 522,
    imageHeight: 435,
    imageX: 189,
    canvasWidth: 900,
    anchors: COLON_ANCHORS,
  },
  gastro: {
    image: '/img/gastro-preview-img.png',
    imageWidth: 357,
    imageHeight: 453,
    imageX: 150,
    canvasWidth: 660,
    anchors: GASTRO_ANCHORS,
  },
}

// Report2 右欄在 1440px viewport 只約 320px 寬；colon SVG 的 900-unit
// viewBox 會縮到 0.356 倍。這些數值用同一份幾何契約同時約束
// 文字、引線與排列，避免只改 CSS 後又在真實版面被縮回微小字。
export const ANATOMY_CALLOUT_METRICS = Object.freeze({
  titleFontSize: 34,
  detailFontSize: 26,
  titleOffsetY: -7,
  detailOffsetY: 27,
  preferredRowGap: 76,
  verticalPadding: 48,
  labelInset: 16,
  lineLabelGap: 8,
  elbowInset: 2,
  railOuterPadding: 8,
})

function normalizedLesions(group) {
  if (Array.isArray(group.lesions)) return group.lesions
  return (group.names || []).map((name) => ({ name, size: '' }))
}

function glyphWidthInEm(character) {
  if (/\s/.test(character)) return 0.34
  if (/[\u0300-\u036f\ufe00-\ufe0f]/.test(character)) return 0
  if (/[⺀-鿿぀-ヿㇰ-ㇿ가-힯！-｠]/.test(character)) return 1
  if (/[ilI1.,:;'|!]/.test(character)) return 0.34
  // Arial 的 M/W/m/w/@ 與部分符號可接近 1em；把它們視為全寬，避免
  // production SVG 的 getComputedTextLength 明顯高於純字元估算。
  if (/[MWmw@#%&QO]/.test(character)) return 1
  if (/[0-9]/.test(character)) return 0.58
  if (/[A-Z]/.test(character)) return 0.76
  if (/[a-z]/.test(character)) return 0.58
  if (character === '·' || character === '-' || character === '/') return 0.44
  return 1
}

// SVG 在 Jest/SSR 沒有可靠 getComputedTextLength；這裡使用與 Arial 接近、對
// CJK/日文與未知 glyph 採 1em 上限的保守度量。實際字寬只會比 rail 預算更小，
// 因此畫面與列印共用同一幾何時不會因語系換字而越界。
export function estimateAnatomyTextWidth(value, fontSize, bold = false) {
  const emWidth = Array.from(String(value || '')).reduce(
    (width, character) => width + glyphWidthInEm(character),
    0
  )
  return Math.ceil(emWidth * fontSize * (bold ? 1.04 : 1))
}

export function anatomyCalloutText(group, maxTextWidth = Infinity) {
  const lesions = normalizedLesions(group)
  const lesionText = lesions
    .map(({ name, size }) => [name, size].filter(Boolean).join(' · '))
    .join(' / ')
  const accessibleTitle = lesionText || 'Lesion'
  const accessibleDetail = group.location || 'Location not documented'
  const primaryLesion = lesions.find(({ name }) => String(name || '').trim())
  const primaryName = primaryLesion ? String(primaryLesion.name).trim() : 'Lesion'
  const primaryFits = estimateAnatomyTextWidth(
    primaryName,
    ANATOMY_CALLOUT_METRICS.titleFontSize,
    true
  ) <= maxTextWidth
  return {
    // 窄 rail 只負責讓醫師看清楚 leader 指到哪種病灶；size/location 與同點的
    // 其他病灶留在 SVG title/ARIA 及正常 Findings。名稱放不下時用完整的中性
    // `Lesion`，不以省略號製造看似具體、實際不完整的臨床標籤。
    title: primaryFits ? primaryName : 'Lesion',
    detail: '',
    accessibleTitle,
    accessibleDetail,
  }
}

function allocateRows(entries, imageHeight) {
  if (!entries.length) return []
  const padding = ANATOMY_CALLOUT_METRICS.verticalPadding
  const minGap = ANATOMY_CALLOUT_METRICS.preferredRowGap
  const placed = entries.map((entry, index) => ({
    ...entry,
    labelY: Math.max(
      Math.min(entry.anchorY, imageHeight - padding),
      padding + index * minGap
    ),
  }))

  const overflow = placed[placed.length - 1].labelY - (imageHeight - padding)
  if (overflow > 0) placed.forEach((entry) => { entry.labelY -= overflow })

  for (let index = placed.length - 2; index >= 0; index -= 1) {
    placed[index].labelY = Math.min(placed[index].labelY, placed[index + 1].labelY - minGap)
  }
  return placed
}

function readableRowCapacity(imageHeight) {
  const { preferredRowGap, verticalPadding } = ANATOMY_CALLOUT_METRICS
  return Math.floor((imageHeight - verticalPadding * 2) / preferredRowGap) + 1
}

function selectReadableEntries(entries, capacity) {
  if (entries.length <= capacity) return entries
  // 保留首尾並等距取樣，避免高密度時只偏向單一解剖區。沒有被畫出的 callout
  // 仍留在回傳值與整張圖的 aria 描述，正常 Findings 也不受影響。
  return Array.from({ length: capacity }, (_, slot) => {
    const index = Math.round(slot * (entries.length - 1) / (capacity - 1))
    return entries[index]
  })
}

/**
 * 將截圖 metadata 分組換成 SVG 可直接畫的點、引線與說明位置。
 * 排版只依解剖點位置決定，因此畫面、列印和重新整理後都一致。
 */
export function buildAnatomyCallouts(groups, kind = 'colon') {
  const config = ANATOMY_MAP_CONFIG[kind] || ANATOMY_MAP_CONFIG.colon
  const centerX = config.imageWidth / 2
  // proximal / middle / distal 可能都映射到同一個解剖點。先合併再排版，避免同一點
  // 疊三條線，也避免 Vue 產生重複 key。
  const byIndex = new Map()
  for (const group of (Array.isArray(groups) ? groups : [])) {
    const index = Number(group.index)
    if (!config.anchors[index]) continue
    if (!byIndex.has(index)) byIndex.set(index, { index, locations: [], lesions: [] })
    const merged = byIndex.get(index)
    if (group.location && !merged.locations.includes(group.location)) merged.locations.push(group.location)
    for (const lesion of normalizedLesions(group)) {
      if (!merged.lesions.some((item) => item.name === lesion.name && item.size === lesion.size)) {
        merged.lesions.push(lesion)
      }
    }
  }

  const mergedGroups = [...byIndex.values()].map((group) => ({
    index: group.index,
    location: group.locations.join(' / '),
    lesions: group.lesions,
  }))
  const entries = mergedGroups
    .map((group) => {
      const anchor = config.anchors[Number(group.index)]
      if (!anchor) return null
      const side = anchor[0] < centerX ? 'left' : 'right'
      return {
        ...group,
        side,
        anchorX: config.imageX + anchor[0],
        anchorY: anchor[1],
      }
    })
    .filter(Boolean)

  const result = []
  for (const side of ['left', 'right']) {
    const sideEntries = entries
      .filter((entry) => entry.side === side)
      .sort((a, b) => a.anchorY - b.anchorY || a.index - b.index)
    const imageEdgeX = side === 'left' ? config.imageX : config.imageX + config.imageWidth
    const direction = side === 'left' ? -1 : 1
    const labelX = imageEdgeX + direction * ANATOMY_CALLOUT_METRICS.labelInset
    const lineEndX = labelX - direction * ANATOMY_CALLOUT_METRICS.lineLabelGap
    const elbowX = imageEdgeX + direction * ANATOMY_CALLOUT_METRICS.elbowInset
    const textAnchor = side === 'left' ? 'end' : 'start'
    const maxTextWidth = side === 'left'
      ? labelX - ANATOMY_CALLOUT_METRICS.railOuterPadding
      : config.canvasWidth - labelX - ANATOMY_CALLOUT_METRICS.railOuterPadding
    const entriesWithText = sideEntries.map((entry) => {
      const text = anatomyCalloutText(entry, maxTextWidth)
      return {
        ...entry,
        ...text,
        maxTextWidth,
        titleWidth: estimateAnatomyTextWidth(
          text.title,
          ANATOMY_CALLOUT_METRICS.titleFontSize,
          true
        ),
        detailWidth: estimateAnatomyTextWidth(
          text.detail,
          ANATOMY_CALLOUT_METRICS.detailFontSize
        ),
      }
    })
    const readableEntries = selectReadableEntries(
      entriesWithText,
      readableRowCapacity(config.imageHeight)
    )
    const placedByIndex = new Map(
      allocateRows(readableEntries, config.imageHeight).map((entry) => [entry.index, entry])
    )
    entriesWithText.forEach((entry) => {
      const placed = placedByIndex.get(entry.index)
      result.push({
        ...(placed || entry),
        visible: Boolean(placed),
        labelY: placed ? placed.labelY : null,
        labelX,
        lineEndX,
        elbowX,
        textAnchor,
      })
    })
  }
  return result.sort((a, b) => a.index - b.index)
}
